<script lang="ts">
	//Design system components
	import {
		Button,
		Container,
		Modal,
		Text,
		toast,
		VerificationCode,
	} from '@Shelly/shelly-design-system';
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from '../../../services/http';
	//Helpers
	import { wait } from 'src/helpers/utilities';
	import { real_id } from 'src/helpers/interface_helpers';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';
	//Hooks
	import { onMount } from 'svelte';

	export let device: Device,
		showModal = false;
	/**
	 * Authentication variables and functions.
	 */

	/**
	 * The one-time code entered by the user.
	 * @type {string}
	 */
	let value = '';

	/**
	 * Whether the authentication is currently in progress.
	 * @type {boolean}
	 */
	let loading: boolean = false;

	/**
	 * A reference to the HTML element containing the authentication code input.
	 */
	let authenticationCodeRef;

	/**
	 * Closes the authentication modal.
	 */
	const onCloseAuthentication = () => {
		showModal = false;
	};

	/**
	 * Authenticates the device using the provided one-time code.
	 * @async
	 */
	const authenticate = async () => {
		/**
		 * Set loading state to true before sending the request.
		 */
		loading = true;

		/**
		 * Send a POST request to the device authentication endpoint.
		 * @param {string} id - The device ID.
		 * @param {string} otcode - The one-time code (uppercase).
		 * @param {string} room - The room ID.
		 */
		const res: SettingRequest = await postReq(`/fast/device/wd`, {
			id: real_id(device.id), //because now we have channels, we need to use the real id
			otcode: value.toUpperCase(),
			room: device.room_id,
		});

		/**
		 * Handle the response from the server.
		 */
		if (res.isok) {
			/**
			 * Display a success toast, extract Components and close the authentication modal.
			 */
			toast.open({ type: 'success', title: $format('wall_display_unlock') });

			onCloseAuthentication();
		} else if (
			res?.errors?.message &&
			(res?.errors?.message.includes('wrong otcode! otcode reset') ||
				res?.errors?.message.includes('no one-time code set atm'))
		) {
			/**
			 * Display an error toast if the one-time code is incorrect or not set.
			 */
			toast.open({
				type: 'danger',
				title: $format('err_auth_wd_toast', {
					default: 'Wrong code. Please try again.',
				}),
			});
			value = '';
		} else {
			/**
			 * Display a generic error toast if the response contains an unknown error.
			 */
			toast.open({ type: 'danger', title: $format('error_global') });
		}

		/**
		 * Set loading state to false after the request has completed.
		 */
		loading = false;
	};

	/**
	 * Called when the component is mounted.
	 * @async
	 */
	onMount(async () => {
		/**
		 * Wait for 10 milliseconds before focusing the authentication code input.
		 */
		await wait(10);
		if (authenticationCodeRef) {
			/**
			 * Focus the authentication code input element.
			 */
			authenticationCodeRef.focus();
		}
	});

	//ToDo need design system improvements
</script>

<Modal
	on:close={onCloseAuthentication}
	{showModal}
	title={$format('title_authenticate_device', {
		default: 'Authenticate device',
	})}
>
	<svelte:fragment slot="content">
		<Container noScroll class="d-flex align-items-center" padding="0">
			<Text element="h4" class="text-center mb-l">
				{$format('wall_display_locked', {
					default: 'Please enter the code from your Shelly Wall Display.',
				})}
			</Text>

			<!-- DO NOT REMOVE COMMENT: Padding references -->
			<VerificationCode
				id="verificationcode-kwfdm4i"
				a11yProps={{
					fieldsAriaLabel: [
						$format('label_verification_code_digit', {
							default: 'Verification code digit 1',
						}),
						$format('label_verification_code_digit', {
							default: 'Verification code digit 2',
						}),
						$format('label_verification_code_digit', {
							default: 'Verification code digit 3',
						}),
						$format('label_verification_code_digit', {
							default: 'Verification code digit 4',
						}),
						$format('label_verification_code_digit', {
							default: 'Verification code digit 5',
						}),
						$format('label_verification_code_digit', {
							default: 'Verification code digit 6',
						}),
					],
				}}
				bind:this={authenticationCodeRef}
				bind:value
				allCapChars
				numberOfFields={6}
				type="text"
				class="pb-s"
				on:value_change={(e) => (value = e.detail.value)}
			/>
		</Container>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-full text-nowrap text-capitalize"
			size="l"
			{loading}
			on:click={authenticate}
		>
			{$format('button_authenticate', { default: 'authenticate' })}
		</Button>
	</div>
</Modal>
